import type { SeriesOption } from 'echarts';
import type { PositiveVsNegativeDataPoint, PositiveVsNegativeChartConfig } from '../types';
import { stackedCornerRadiusFor } from '../shared/barRadius';
import { resolveItemOpacity } from '../shared/selection';

/** Shared stack id for the income/spending bars — the combined line series stays unstacked. */
const POSITIVE_VS_NEGATIVE_STACK = 'positiveVsNegative';

export const buildPositiveVsNegativeSeries = (
  data: PositiveVsNegativeDataPoint[],
  config: PositiveVsNegativeChartConfig,
): SeriesOption[] => {
  const {
    positiveLabel = 'Income',
    negativeLabel = 'Expenses',
    combinedLabel = 'Net Income',
    positiveColor = config.theme.colors[0],
    negativeColor = config.theme.colors[3],
    combinedColor = config.theme.colors[1],
    selectedIndex,
    hoveredIndex,
  } = config;
  const activeIndex = hoveredIndex ?? selectedIndex;

  return [
    {
      name: positiveLabel,
      type: 'bar',
      // Both bars share one stack so they occupy a single column per category instead of sitting
      // side by side. ECharts' default 'samesign' stack strategy keeps them on opposite sides of
      // zero: income stacks upward, spending (negative) downward.
      stack: POSITIVE_VS_NEGATIVE_STACK,
      barGap: '0%',
      data: data.map((d, i) => ({
        value: d.positiveTotal,
        itemStyle: {
          color: positiveColor,
          // Income is stacked first, so spending takes the outer edge whenever it shares income's sign.
          borderRadius: stackedCornerRadiusFor(d.positiveTotal, d.negativeTotal),
          opacity: resolveItemOpacity(i, activeIndex),
        },
      })),
      emphasis: { disabled: true },
    },
    {
      name: negativeLabel,
      type: 'bar',
      stack: POSITIVE_VS_NEGATIVE_STACK,
      barGap: '0%',
      data: data.map((d, i) => ({
        value: d.negativeTotal,
        itemStyle: {
          color: negativeColor,
          // Spending is stacked last, so its tip is always the outer edge of the stack.
          borderRadius: stackedCornerRadiusFor(d.negativeTotal),
          opacity: resolveItemOpacity(i, activeIndex),
        },
      })),
      emphasis: { disabled: true },
    },
    {
      name: combinedLabel,
      type: 'line',
      data: data.map((d, i) => ({
        value: d.combinedTotal,
        itemStyle: {
          color: combinedColor,
          borderColor: config.theme.bgColor,
          borderWidth: 2,
          opacity: resolveItemOpacity(i, activeIndex),
        },
      })),
      lineStyle: { color: combinedColor, width: 2, type: 'dashed' },
      symbol: 'circle',
      symbolSize: 8,
      emphasis: { disabled: true },
    },
  ];
};
